import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Box,
  Text,
  Anchor,
  Button,
} from 'grommet';
import { SimpleModal } from '@Components/Control';
import { shortenInt } from '@Helpers/util';

export const StyledButton = styled(Button)`
  text-align: start;
  background-color: var(--button-table-bg);
  border-radius: 6px;
  padding: ${({ small }) => (small ? '0.35rem 0.6rem' : '0.25rem 0.5rem')};
  width: fit-content;
  border: 1px solid transparent;
  cursor: pointer;

  &:hover {
    border: 1px solid var(--page-focus-highlight);
  }

  &:focus {
    outline: none;
    border: 1px solid var(--page-focus-highlight);
  }

  ${({ isOpen }) => isOpen && `
    border: 1px solid var(--page-focus-highlight);
  `}
`;

const VideoDetails = ({
  small,
  videoTitle,
  videoId,
  videoViews,
  channelDisplayName,
  channelId,
  textColor = 'var(--page-modal-text)',
  buttonHighlight = 'var(--page-highlight)',
  fallbackHeader = 'Video details',
}) => {
  const [showModal, setShowModal] = React.useState(false);

  return (
    <Box>
      <StyledButton
        small={small}
        isOpen={showModal}
        onClick={() => setShowModal(true)}
      >
        <Text size={small ? '1rem' : '0.9rem'} weight={500} color="var(--button-table-text)">
          {videoId ? `https://www.youtube.com/watch?v=${videoId}` : fallbackHeader}
        </Text>
      </StyledButton>
      {showModal && (
        <SimpleModal
          hideLowerSection
          small={small}
          toggleModal={setShowModal}
          modalTitle="Video details"
          textColor={textColor}
          highlightColor={buttonHighlight}
          modalBg="var(--page-modal-bg)"
          titleSize="1.2rem"
          desktopMinWidth="30rem"
        >
          <Box direction="column" gap="0.75rem" pad={{ vertical: '0.5rem' }}>
            {videoTitle && (
              <Box direction="row" gap="0.5rem" align="center">
                <Text size="1rem" color={textColor} weight={500} style={{ wordBreak: 'keep-all' }}>Title:</Text>
                <Text color={textColor} size="1rem">{videoTitle}</Text>
              </Box>
            )}
            <Box direction="row" gap="0.5rem" align="center">
              <Text size="1rem" color={textColor} weight={500}>Video:</Text>
              {videoId ? (
                <Anchor
                  href={`https://www.youtube.com/watch?v=${videoId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  color={buttonHighlight}
                  style={{ fontWeight: 600 }}
                >
                  {`https://www.youtube.com/watch?v=${videoId}`}
                </Anchor>
              ) : (
                <Text color={textColor} size="1rem">N/A</Text>
              )}
            </Box>
            <Box direction="row" gap="0.5rem" align="center">
              <Text size="1rem" color={textColor} weight={500}>Channel:</Text>
              {channelId ? (
                <Anchor
                  href={`https://www.youtube.com/channel/${channelId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  color={buttonHighlight}
                  style={{ fontWeight: 600 }}
                >
                  {channelDisplayName || `https://www.youtube.com/channel/${channelId}`}
                </Anchor>
              ) : (
                <Text color={textColor} size="1rem">N/A</Text>
              )}
            </Box>
            <Box direction="row" gap="0.5rem" align="center">
              <Text size="1rem" color={textColor} weight={500}>Views:</Text>
              <Text color={textColor} size="1rem">{shortenInt(videoViews) || 'N/A'}</Text>
            </Box>
          </Box>
        </SimpleModal>
      )}
    </Box>
  );
};

VideoDetails.propTypes = {
  small: PropTypes.bool.isRequired,
  fallbackHeader: PropTypes.string,
  videoTitle: PropTypes.string,
  videoId: PropTypes.string,
  videoViews: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  textColor: PropTypes.string,
  buttonHighlight: PropTypes.string,
  channelDisplayName: PropTypes.string,
  channelId: PropTypes.string,
};

export default VideoDetails;
